import { EditorState } from 'draft-js'
import {
  AsyncState,
  Updater,
  loadedAsyncState,
  unloadedAsyncState,
} from 'widgets-for-react'

import { ComponentData } from '../componentDetail/componentDetailState'
import {
  loadEditComponent,
  loadData,
  loadComponentEditState,
  loadCreateComponent,
  loadDeleteComponent,
} from './componentEditApi'
import { htmlStringToEditorState, isDraft } from './componentEditorUtils'

export type ComponentEditState = AsyncState<ComponentEditorState>

export type ComponentEditorState = {
  newComponent: boolean //are we going to create a new component, or editing an existing user component?
  originalComponentName: string
  editingState: AsyncState<number | {}>
  deletingState: AsyncState<{}>
  componentFields: {
    [field in keyof ComponentFields]: ComponentFields[field]
  }
  dataState: AsyncState<Data>
  warning?: 'delete' | 'cancel'
  scrollTo?: HTMLSpanElement
  amountOfNewIngredients: number
}

export type ComponentFields = {
  name: string
  introduction: EditorState
  preparation: EditorState
  servingSuggestions: EditorState
  ingredients: Ingredient[]
  mainIngredients: { id: number; translation: string }[]
  techniques: { id: number; translation: string }[]
  appearances: { id: number; translation: string }[]
  textures: { id: number; translation: string }[]
  worldFlavours: { id: number; translation: string }[]
}

type Ingredient = {
  id: number
  amount: string
  prepreparations: { id: number; translation: string }[]
  ingredient: { id: number; translation: string }
  unitOfMeasurement: { id: number; translation: string }
}

type Data = {
  techniques: { id: number; translation: string }[]
  appearances: { id: number; translation: string }[]
  textures: { id: number; translation: string }[]
  worldFlavours: { id: number; translation: string }[]
  prepreparations: { id: number; translation: string }[]
  unitsOfMeasurement: { id: number; translation: string }[]
}

export const initialComponentEditState = (
  componentId: number,
  componentData?: ComponentData
): ComponentEditState =>
  componentData
    ? loadedAsyncState(initialComponentEditorState(componentData))
    : loadComponentEditState(componentId)

export const initialComponentEditorState = (
  componentData: ComponentData
): ComponentEditorState => {
  const {
    Name,
    Introduction,
    Preparation,
    ServingSuggestions,
    mainIngredients,
    ingredients,
    techniques,
    appearances,
    textures,
    worldFlavours,
    UserId,
  } = componentData

  return {
    newComponent: UserId === '', // when there is no user id, it means that it is a GSX component, so we will create a new one
    originalComponentName: Name,
    editingState: unloadedAsyncState(),
    deletingState: unloadedAsyncState(),
    componentFields: {
      name: Name,
      introduction: htmlStringToEditorState(Introduction),
      preparation: htmlStringToEditorState(Preparation),
      servingSuggestions: htmlStringToEditorState(ServingSuggestions),
      mainIngredients: mainIngredients,
      ingredients: ingredients,
      techniques,
      appearances,
      textures,
      worldFlavours,
    },
    dataState: loadData,
    amountOfNewIngredients: 0,
  }
}

export const componentEditStateUpdaters = {
  componentEditorStateUpdater:
    (updater: Updater<ComponentEditorState>) =>
    (state: ComponentEditState): ComponentEditState =>
      state.kind === 'loaded' ? loadedAsyncState(updater(state.value)) : state,
  dataStateUpdater:
    (updater: Updater<AsyncState<Data>>) =>
    (state: ComponentEditorState): ComponentEditorState => ({
      ...state,
      dataState: updater(state.dataState),
    }),
  componentFieldValueUpdater:
    <K extends keyof ComponentFields>(k: K) =>
    (updater: Updater<ComponentFields[K]>) =>
    (state: ComponentEditorState): ComponentEditorState => ({
      ...state,
      componentFields: {
        ...state.componentFields,
        [k]: updater(state.componentFields[k]),
      },
    }),
  selectedUpdater:
    (item: {
      id: number
      translation: string
    }): Updater<{ id: number; translation: string }[]> =>
    (selected): { id: number; translation: string }[] =>
      selected.map((s) => s.id).includes(item.id)
        ? selected.filter((s) => s.id !== item.id)
        : [...selected, item],
  arrayUpdater:
    <T>(index: number) =>
    (updater: Updater<T>) =>
    (state: T[]): T[] =>
      state.map((item, i) => (i === index ? updater(item) : item)),
  removeIngredientUpdater: (index: number) =>
    componentEditStateUpdaters.componentFieldValueUpdater('ingredients')(
      (state) => state.filter((_, i) => i !== index)
    ),
  addIngredientUpdater: (
    state: ComponentEditorState
  ): ComponentEditorState => ({
    ...componentEditStateUpdaters.componentFieldValueUpdater('ingredients')(
      (ingredients) => [
        ...ingredients,
        {
          id: -(state.amountOfNewIngredients + 1),
          amount: '',
          prepreparations: [],
          unitOfMeasurement: { id: 0, translation: '' },
          ingredient: {
            id: 0,
            translation: '',
            term: '',
            searchResultsState: unloadedAsyncState(),
          },
        },
      ]
    )(state),
    amountOfNewIngredients: state.amountOfNewIngredients + 1,
  }),
  moveIngredientUpdater:
    (index: number, action: 'up' | 'down', element?: HTMLSpanElement) =>
    (state: ComponentEditorState): ComponentEditorState => ({
      ...componentEditStateUpdaters.componentFieldValueUpdater('ingredients')(
        (state) => {
          const ingredientAtIndex = state[index]
          const newIndex = action === 'up' ? index - 1 : index + 1
          const ingredientAtNewIndex = state[newIndex]
          if (ingredientAtIndex && ingredientAtNewIndex) {
            const newState = [...state]
            newState[index] = ingredientAtNewIndex
            newState[newIndex] = ingredientAtIndex
            return newState
          }
          return state
        }
      )(state),
      scrollTo: element || undefined,
    }),
  ingredientsAmountUpdater: (ingredientIndex: number) => (newValue: string) =>
    componentEditStateUpdaters.componentFieldValueUpdater('ingredients')(
      componentEditStateUpdaters.arrayUpdater<Ingredient>(ingredientIndex)(
        (state) => ({ ...state, amount: newValue })
      )
    ),
  ingredientsPrepreparationsUpdater:
    (ingredientIndex: number) => (item: { id: number; translation: string }) =>
      componentEditStateUpdaters.componentFieldValueUpdater('ingredients')(
        componentEditStateUpdaters.arrayUpdater<Ingredient>(ingredientIndex)(
          (state) => ({
            ...state,
            prepreparations: componentEditStateUpdaters.selectedUpdater(item)(
              state.prepreparations
            ),
          })
        )
      ),
  ingredientsUnitOfMeasurementUpdater:
    (ingredientIndex: number) =>
    (value: string) =>
    (state: ComponentEditorState): ComponentEditorState => {
      const dataState = state.dataState
      const unitOfMeasurement =
        dataState.kind === 'loaded'
          ? dataState.value.unitsOfMeasurement.find(
              (u) => u.id === parseInt(value)
            )
          : undefined
      return componentEditStateUpdaters.componentFieldValueUpdater(
        'ingredients'
      )(
        componentEditStateUpdaters.arrayUpdater<Ingredient>(ingredientIndex)(
          (state) => ({
            ...state,
            unitOfMeasurement: unitOfMeasurement || {
              id: 0,
              translation: '',
            },
          })
        )
      )(state)
    },
  ingredientsIngredientUpdater:
    (ingredientIndex: number) =>
    (updater: Updater<{ id: number; translation: string }>) =>
      componentEditStateUpdaters.componentFieldValueUpdater('ingredients')(
        componentEditStateUpdaters.arrayUpdater<Ingredient>(ingredientIndex)(
          (state) => ({ ...state, ingredient: updater(state.ingredient) })
        )
      ),
  selectedIngredientSetter: (ingredientIndex: number) => (selected?: {id: number, translation: string}) =>
    componentEditStateUpdaters.ingredientsIngredientUpdater(ingredientIndex)(
      (state) => {
        return {
          ...state,
          id: selected?.id || 0,
          translation: selected?.translation || '',
        }
      }
    ),
  startLoadingEditingStateUpdater:
    (componentId: number) =>
    (state: ComponentEditorState): ComponentEditorState => ({
      ...state,
      editingState: state.newComponent
        ? loadCreateComponent(
            state.componentFields,
            isDraft(state.componentFields),
            componentId
          )
        : loadEditComponent(
            state.componentFields,
            isDraft(state.componentFields),
            componentId
          ),
    }),
  editingStateUpdater:
    (updater: Updater<AsyncState<number | {}>>) =>
    (state: ComponentEditorState): ComponentEditorState => ({
      ...state,
      editingState: updater(state.editingState),
    }),
  startLoadingDeletingStateUpdater:
    (componentId: number) =>
    (state: ComponentEditorState): ComponentEditorState => ({
      ...state,
      deletingState: loadDeleteComponent(componentId),
    }),
  deletingStateUpdater:
    (updater: Updater<AsyncState<{}>>) =>
    (state: ComponentEditorState): ComponentEditorState => ({
      ...state,
      deletingState: updater(state.deletingState),
    }),
  warningUpdater:
    (warning?: 'delete' | 'cancel') =>
    (state: ComponentEditorState): ComponentEditorState => ({
      ...state,
      warning: warning,
    }),
}
